// src/components/CampaignWizard/StepCost.tsx
import React, { useEffect, useState } from "react";
import {
	Row,
	Col,
	Card,
	CardBody,
	CardTitle,
	Progress,
	Tooltip,
} from "reactstrap";

export interface CostEstimate {
	emailCost: number;
	smsCost: number;
	inAppCost: number;
	totalCost: number;
}

export interface StepCostProps {
	data: CostEstimate;
	setData: (c: CostEstimate) => void;
	onNext: () => void;
	onBack: () => void;
}

const StepCost: React.FC<StepCostProps> = ({
	data,
	setData,
	onNext,
	onBack,
}) => {
	// tooltip toggles
	const [tips, setTips] = useState<Record<string, boolean>>({
		email: false,
		sms: false,
		inApp: false,
	});
	const toggleTip = (k: keyof typeof tips) =>
		setTips((t) => ({ ...t, [k]: !t[k] }));

	useEffect(() => {
		// stub: hard-coded rates × volumes
		const emailCost = 0.01 * 1000;
		const smsCost = 0.005 * 500;
		const inAppCost = 0.0;
		setData({
			emailCost,
			smsCost,
			inAppCost,
			totalCost: emailCost + smsCost + inAppCost,
		});
	}, [setData]);

	// percentages for progress bar
	const { emailCost, smsCost, inAppCost, totalCost } = data;
	const pct = (c: number) =>
		totalCost ? Math.round((c / totalCost) * 100) : 0;

	return (
		<>
			<Row className="mb-4">
				<Col>
					<h4 className="fw-bold">Campaign Cost Estimate</h4>
					<p className="text-muted">
						Here’s a breakdown by channel. Hover over each to learn more.
					</p>
				</Col>
			</Row>

			{/* ── Stacked progress bar ─────────────────────── */}
			<Row className="mb-5">
				<Col>
					<Progress multi style={{ height: 24, borderRadius: 12 }}>
						<Progress
							bar
							color="primary"
							value={pct(emailCost)}
							style={{ borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }}
						>
							{pct(emailCost)}% Email
						</Progress>
						<Progress bar color="success" value={pct(smsCost)}>
							{pct(smsCost)}% SMS
						</Progress>
						<Progress
							bar
							color="warning"
							value={pct(inAppCost)}
							style={{ borderTopRightRadius: 12, borderBottomRightRadius: 12 }}
						>
							{pct(inAppCost)}% In-App
						</Progress>
					</Progress>
				</Col>
			</Row>

			{/* ── Detailed cards ──────────────────────────── */}
			<Row className="gx-4 mb-5">
				{[
					{
						key: "email",
						icon: "ri-mail-line",
						label: "Email",
						value: emailCost,
						info: "0.01 USD × 1,000 recipients",
						color: "primary",
					},
					{
						key: "sms",
						icon: "ri-message-2-line",
						label: "SMS",
						value: smsCost,
						info: "0.005 USD × 500 recipients",
						color: "success",
					},
					{
						key: "inApp",
						icon: "ri-smartphone-line",
						label: "In-App",
						value: inAppCost,
						info: "No charge",
						color: "warning",
					},
					{
						key: "total",
						icon: "ri-equal-line",
						label: "Total",
						value: totalCost,
						info: "Sum of all channels",
						color: "success",
					},
				].map(({ key, icon, label, value, info, color }) => (
					<Col key={key} md={3} sm={6} xs={12}>
						<Card className="shadow-sm text-center">
							<CardBody>
								<i
									id={`tip-${key}`}
									className={`${icon} text-${color} fs-2 mb-2`}
									style={{ cursor: "pointer" }}
									onMouseEnter={() => toggleTip(key as any)}
									onMouseLeave={() => toggleTip(key as any)}
								/>
								<Tooltip
									target={`tip-${key}`}
									isOpen={tips[key as keyof typeof tips]}
									placement="top"
								>
									{info}
								</Tooltip>
								<CardTitle tag="h6" className="mb-2">
									{label}
								</CardTitle>
								<div className="fs-5 fw-semibold">
									${value.toFixed(2)}
								</div>
							</CardBody>
						</Card>
					</Col>
				))}
			</Row>
		</>
	);
};

export default StepCost;
